import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Eye, Edit, MoreHorizontal, LogIn, Lock, Trash2, MailCheck,
} from "lucide-react";
import { badgeStyle } from "@/utils/szStatus";
import { getApprovalConfig, approvalOptions, formatDate, avatarColor, userInitials, userDisplayName } from "./userConstants";

const GRID = "grid items-center gap-3 [grid-template-columns:42px_1.5fr_1.6fr_1.3fr_130px_110px_120px]";

const UsersTable = ({
  users, selectedUsers, onToggleSelect, onToggleSelectAll,
  onViewProfile, onRemoveBounce, onLoginAs, onEdit, onPasswordReset, onDelete, onApprovalChange,
}) => {
  const allChecked = users.length > 0 && users.every((u) => selectedUsers.has(u.id));

  return (
    <div className="hidden md:block sz-card overflow-hidden">
      {/* Header row */}
      <div className={`${GRID} px-[18px] py-[13px] bg-[#FAFBFC] border-b border-[#EEF0F4] sz-overline text-[#8A90A2]`}>
        <div>
          <input
            type="checkbox"
            checked={allChecked}
            onChange={onToggleSelectAll}
            className="w-4 h-4 cursor-pointer accent-[#572B7C]"
            aria-label="Select all"
          />
        </div>
        <div>User</div>
        <div>Contact</div>
        <div>Business</div>
        <div>Status</div>
        <div>Joined</div>
        <div className="text-right">Actions</div>
      </div>

      {/* Rows */}
      {users.map((user) => {
        const isSelected = selectedUsers.has(user.id);
        const cfg = getApprovalConfig(user.approval_status || "new");
        return (
          <div
            key={user.id}
            className={`${GRID} px-[18px] py-3 border-b border-[#F1F3F7] transition-colors ${isSelected ? "bg-[#F6F2FB]" : "hover:bg-[#FAFBFC]"}`}
          >
            {/* Checkbox */}
            <div>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelect(user.id)}
                className="w-4 h-4 cursor-pointer accent-[#572B7C]"
                aria-label="Select row"
              />
            </div>

            {/* User */}
            <div className="flex items-center gap-[11px] min-w-0">
              <div
                className="w-[38px] h-[38px] rounded-full text-white flex items-center justify-center font-bold text-[13px] shrink-0 overflow-hidden"
                style={{ background: avatarColor(user.id || user.email), fontFamily: "Poppins" }}
              >
                {user.profile_photo_url
                  ? <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover" />
                  : userInitials(user)}
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[#0C1120] truncate">
                  {userDisplayName(user)}
                </div>
                <div className="text-[11.5px] text-[#8A90A2] sz-mono">USR-{user.user_number}</div>
              </div>
            </div>

            {/* Contact */}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[12.5px] text-[#2B3142] min-w-0">
                {user.email_bounced && (
                  <span title="Email bounced" className="w-[7px] h-[7px] rounded-full bg-[#EA580C] shrink-0" />
                )}
                <span className={`truncate ${user.email_bounced ? "text-[#EA580C]" : ""}`}>{user.email}</span>
              </div>
              {user.phone && <div className="text-[11.5px] text-[#8A90A2] mt-0.5">{user.phone}</div>}
            </div>

            {/* Business */}
            <div className="min-w-0">
              <div className="text-[12.5px] text-[#2B3142] truncate">{user.business_name || "—"}</div>
              {user.abn && <div className="text-[11.5px] text-[#8A90A2] sz-mono">ABN {user.abn}</div>}
            </div>

            {/* Status — click to change */}
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="sz-badge cursor-pointer hover:opacity-90" style={badgeStyle(cfg)}>
                    {cfg.label}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {approvalOptions.map((opt) => {
                    const OptIcon = opt.icon;
                    return (
                      <DropdownMenuItem key={opt.key} onClick={() => onApprovalChange(user.id, opt.status, opt.notify)}>
                        <OptIcon className={`w-4 h-4 mr-2 ${opt.iconColor}`} />{opt.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Joined */}
            <div className="text-xs text-[#2B3142]">{formatDate(user.created_at)}</div>

            {/* Actions */}
            <div className="flex gap-1.5 justify-end">
              <button
                onClick={() => onViewProfile(user)}
                title="View profile"
                className="w-[31px] h-[31px] border border-[#E0E3EB] rounded-lg bg-white text-[#2B3142] flex items-center justify-center hover:border-[#28C0D0] hover:text-[#0C1120] transition-colors"
              >
                <Eye className="w-[15px] h-[15px]" />
              </button>
              <button
                onClick={() => onEdit(user)}
                title="Edit"
                className="w-[31px] h-[31px] border border-[#E0E3EB] rounded-lg bg-white text-[#2B3142] flex items-center justify-center hover:border-[#28C0D0] hover:text-[#0C1120] transition-colors"
              >
                <Edit className="w-[15px] h-[15px]" />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    title="More"
                    className="w-[31px] h-[31px] border border-[#E0E3EB] rounded-lg bg-white text-[#2B3142] flex items-center justify-center hover:border-[#28C0D0] hover:text-[#0C1120] transition-colors"
                  >
                    <MoreHorizontal className="w-[15px] h-[15px]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => onLoginAs(user)}><LogIn className="w-4 h-4 mr-2 text-blue-500" />Login As User</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onPasswordReset(user.id, user.email)}><Lock className="w-4 h-4 mr-2 text-gray-500" />Password Reset</DropdownMenuItem>
                  {user.email_bounced && (
                    <DropdownMenuItem onClick={() => onRemoveBounce(user.id, user.full_name || user.email)}>
                      <MailCheck className="w-4 h-4 mr-2 text-orange-500" />Remove Bounce
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(user.id, user.email)} className="text-red-600 focus:text-red-600 focus:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-2" />Delete User
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UsersTable;
